/**
 * GIF Exporter
 * Captures animated canvas frames and encodes them as GIF
 */

import GIF from 'gif.js';

export interface GifExportOptions {
  fps: number;
  quality: number; // 1-30, lower is better quality
  scale: number;
  duration: number; // Total duration in ms
  loop: boolean;
  onProgress?: (progress: number) => void;
}

const DEFAULT_OPTIONS: GifExportOptions = {
  fps: 15,
  quality: 10,
  scale: 1,
  duration: 3000,
  loop: true,
};

/**
 * Export canvas animation as GIF
 *
 * @param canvasElement - The canvas element to capture
 * @param seekTo - Function to seek animation to a specific time (ms)
 * @param options - Export options
 * @returns Promise resolving to GIF blob
 */
export async function exportToGIF(
  canvasElement: HTMLElement,
  seekTo: (time: number) => void,
  options: Partial<GifExportOptions> = {}
): Promise<Blob> {
  const opts = { ...DEFAULT_OPTIONS, ...options };
  const { fps, quality, scale, duration, loop, onProgress } = opts;

  // Calculate frame count and interval
  const frameCount = Math.ceil((duration / 1000) * fps);
  const frameDelay = 1000 / fps;

  // Create GIF encoder
  // Note: gif.js worker needs to be available at this path
  const gif = new GIF({
    workers: 2,
    quality,
    width: Math.round(canvasElement.offsetWidth * scale),
    height: Math.round(canvasElement.offsetHeight * scale),
    workerScript: '/gif.worker.js',
    repeat: loop ? 0 : -1, // 0 = loop forever, -1 = no loop
  });

  // Dynamic import html2canvas
  const html2canvas = (await import('html2canvas')).default;

  // Capture frames
  for (let i = 0; i < frameCount; i++) {
    const time = (i / frameCount) * duration;

    // Seek to this frame's time
    seekTo(time);

    // Wait a tick for the DOM to update
    await new Promise(resolve => requestAnimationFrame(resolve));
    await new Promise(resolve => setTimeout(resolve, 16)); // Extra delay for complex renders

    // Capture the frame
    const frameCanvas = await html2canvas(canvasElement, {
      scale,
      backgroundColor: null,
      logging: false,
      useCORS: true,
    });

    // Add frame to GIF
    gif.addFrame(frameCanvas, {
      delay: frameDelay,
      copy: true,
    });

    // Report progress
    if (onProgress) {
      onProgress((i + 1) / frameCount * 0.8); // 80% for frame capture
    }
  }

  // Reset to start
  seekTo(0);

  // Render GIF
  return new Promise((resolve, reject) => {
    gif.on('finished', (blob: Blob) => {
      if (onProgress) onProgress(1);
      resolve(blob);
    });

    gif.on('progress', (p: number) => {
      if (onProgress) {
        onProgress(0.8 + p * 0.2); // Last 20% for encoding
      }
    });

    gif.render();
  });
}

/**
 * Download GIF blob as file
 */
export function downloadGIF(blob: Blob, filename: string): void {
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename.endsWith('.gif') ? filename : `${filename}.gif`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}
